/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import { css, jsx } from '@emotion/core'
import Button from '@material-ui/core/Button'
import PropTypes from 'prop-types'
import { AnalyticsEvents } from '../../../constants'
import localStorageService from '../../../services/localStorage'
import amplitude from '../../../analytics/amplitude'

const styles = {}

styles.container = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  padding: 30px;
  border: #cecece 1px solid;
  border-radius: 10px;
  @media (max-width: 600px) {
    width: 85%;
    padding-bottom: 0;
  }
`

styles.textContainer = css`
  text-align: center;
  font-size: 16px !important;
`

styles.boldText = css`
  font-weight: bold;
`

styles.downloadContainer = css`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 70%;
  @media (max-width: 600px) {
    width: 90%;
  }
`

styles.panel = css`
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px 0;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  @media (max-width: 600px) {
    width: 100%;
  }
`

styles.versionLabel = css`
  margin-top: 20px;
  color: #fff;
  font-size: 10px;
  @media (max-width: 600px) {
    font-size: 9px;
  }
`

styles.buttonsContainer = css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 10px;
`

styles.downloadButton = {
  width: 210,
  textTransform: 'capitalize',
  borderRadius: '8px',
  paddingTop: 10,
  paddingBottom: 10,
  fontSize: '16px'
}

styles.downloadButtonLeft = {
  width: 210,
  textTransform: 'capitalize',
  borderRadius: '8px',
  paddingTop: 10,
  paddingBottom: 10,
  fontSize: '16px'
}

styles.mobileDownloadButton = {
  width: 130,
  borderRadius: 0,
  paddingTop: 10,
  paddingBottom: 10,
  fontSize: 11,
  textAlign: 'center',
  marginBottom: 30
}

styles.downloadZone = css`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  height: 100%;
  z-index: 100;
  gap: 30px;

  @media (max-width: 600px) {
    width: 100%;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    gap: 0;
  }
`

styles.productLogoContainer = css`
  min-height: 60px;
  margin-bottom: 10px;
`

styles.productLogo = css`
  height: 43px;
`

const osMac = 'mac'
const osWindows = 'windows'

function DownloadBoxes(props) {
  const [hoverButton, setHover] = React.useState('')
  const [, setClick] = React.useState('')

  const isHoverMac = hoverButton === osMac
  const isHoverWindows = hoverButton === osWindows

  return (
    <div
      css={styles.container}
      style={{ borderColor: props.borderColor, background: props.background }}
    >
      {/* App logo. */}
      <div css={styles.productLogoContainer}>
        <img css={styles.productLogo} src={props.logo} />
      </div>

      <div css={styles.textContainer}>
        {window.I18n.t(`${props.baseKeyTranslations}.description`)}
      </div>

      {/* Download links. */}
      <div css={styles.downloadZone}>
        {/* MacOS download. */}
        <div css={styles.downloadContainer}>
          <div
            css={css`
              ${styles.panel};
              border-color: ${isHoverMac ? '#fff' : 'rgba(0, 0, 0, 0.42)'};
            `}
          >
            <div css={styles.buttonsContainer}>
              <span
                onMouseOver={onButtonHover(osMac, setHover, setClick)}
                onMouseOut={onButtonHover('', setHover, setClick)}
                onClick={download(osMac, setClick)}
              >
                <Button
                  variant="contained"
                  color="primary"
                  style={{ ...styles.downloadButton, ...props.styleButtons }}
                  href={props.mac.path}
                  data-cta-label={`${props.ctaLabelPrefix}_macos`}
                >
                  {`MacOS ${props.mac.version}`}
                </Button>
              </span>
            </div>
          </div>
        </div>

        {/* Windows download. */}
        <div css={styles.downloadContainer}>
          <div
            css={css`
              ${styles.panel};
              border-color: ${isHoverWindows ? '#fff' : 'rgba(0, 0, 0, 0.42)'};
            `}
          >
            <div css={styles.buttonsContainer}>
              <span
                onMouseOver={onButtonHover(osWindows, setHover, setClick)}
                onMouseOut={onButtonHover('', setHover, setClick)}
                onClick={download(osWindows, setClick)}
              >
                <Button
                  variant="contained"
                  color="primary"
                  style={{ ...styles.downloadButtonLeft, ...props.styleButtons }}
                  href={props.windows.path}
                  data-cta-label={`${props.ctaLabelPrefix}_windows`}
                >
                  {`Windows ${props.windows.version}`}
                </Button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function onButtonHover(system, setHover, setClick) {
  return () => {
    setHover(system)
    setClick('')
  }
}

function registerAnalytics(event) {
  const user = localStorageService.get('user')
  if (user) {
    user.organizations = [{ priority: 10, organization: { code: user.organization } }]
    user.licence_configuration = { base_template: { code: user.licence } }

    amplitude.setUser(user)
    amplitude.logEvent(event)
  }
}

function download(system, setClick) {
  return () => {
    setClick(system)
    registerAnalytics(
      system === osMac ? AnalyticsEvents.APP_DOWNLOADED_MAC : AnalyticsEvents.APP_DOWNLOADED_WIN
    )
  }
}

DownloadBoxes.propTypes = {
  logo: PropTypes.string.isRequired,
  mac: PropTypes.object.isRequired,
  windows: PropTypes.object.isRequired,
  styleButtons: PropTypes.object.isRequired,
  baseKeyTranslations: PropTypes.string.isRequired,
  // Analytics CTA label prefix, e.g. 'nexus' -> nexus_macos, nexus_windows.
  ctaLabelPrefix: PropTypes.string.isRequired,
  borderColor: PropTypes.string,
  background: PropTypes.string
}

export default DownloadBoxes
